import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDown, Check, type LucideIcon } from 'lucide-react'

export interface StyledSelectOption {
  value: string
  label: string
  icon?: LucideIcon
  description?: string
  disabled?: boolean
}

interface StyledSelectProps {
  value: string
  onChange: (value: string) => void
  options: StyledSelectOption[]
  className?: string
  disabled?: boolean
}

/**
 * Styled select replacement using Radix DropdownMenu.
 * Replaces native <select> elements with themed, consistent dropdown styling.
 * Supports optional icon and description per option.
 */
export function StyledSelect({ value, onChange, options, className = '', disabled = false }: StyledSelectProps) {
  const [open, setOpen] = useState(false)
  const selectedOption = options.find(o => o.value === value)
  const hasDescriptions = options.some(o => o.description)

  return (
    <DropdownMenu.Root open={open} onOpenChange={disabled ? undefined : setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 pl-3 pr-2 py-2 rounded-lg border border-border bg-background
            hover:border-foreground/20 focus:outline-none focus:ring-2 focus:ring-primary text-sm
            transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
        >
          {selectedOption?.icon && <selectedOption.icon className="w-4 h-4 text-muted-foreground shrink-0" />}
          <span className="truncate">{selectedOption?.label || value}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`bg-card border border-border rounded-lg shadow-lg py-1 z-50 overflow-y-auto max-h-[var(--radix-dropdown-menu-content-available-height)] ${hasDescriptions ? 'min-w-[280px]' : 'min-w-[140px]'}`}
          align="start"
          sideOffset={4}
          collisionPadding={8}
          avoidCollisions
        >
          {options.map((option) => {
            const Icon = option.icon
            const isActive = option.value === value
            const isDisabled = option.disabled === true
            return (
              <DropdownMenu.Item
                key={option.value}
                disabled={isDisabled}
                className={`px-3 py-1.5 text-sm flex items-center gap-2 outline-none
                  ${isDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : isActive
                      ? 'bg-primary/10 text-primary font-medium cursor-pointer'
                      : 'hover:bg-secondary text-foreground cursor-pointer'
                  }`}
                onSelect={() => { onChange(option.value); setOpen(false) }}
              >
                {Icon && <Icon className="w-4 h-4 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div>{option.label}</div>
                  {option.description && (
                    <div className={`text-xs ${isDisabled ? 'text-muted-foreground' : isActive ? 'text-primary/70' : 'text-muted-foreground'}`}>
                      {option.description}
                    </div>
                  )}
                </div>
                {isActive && <Check className="w-3.5 h-3.5 shrink-0" />}
              </DropdownMenu.Item>
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
